/**
 * Armazenamento de saves da carreira solo.
 *
 * Vive dentro do Web Worker de propósito: o mundo serializado tem alguns
 * megabytes e atravessar isso para a thread da interface a cada rodada seria
 * desperdício. O worker grava e lê direto, e só os metadados pequenos viajam.
 *
 * Isto é exclusivo do modo solo. No modo online quem guarda o mundo é o
 * servidor, e o cliente só recebe a visão filtrada da liga.
 *
 * IndexedDB puro, sem biblioteca: a API é verbosa mas o contrato é estável e
 * evita uma dependência para guardar meia dúzia de registros.
 *
 * As falhas saem por chave, e não por frase, pelo mesmo motivo do resto desta
 * pasta: elas nascem dentro do Web Worker, que nunca soube o idioma escolhido.
 */

import { ErroComChave } from "./types";

const DB_NAME = "legacy-manager";
const DB_VERSION = 1;
const STORE = "saves";

export interface SaveMeta {
  id: string;
  clubName: string;
  managerName: string;
  season: number;
  round: number;
  totalRounds: number;
  updatedAt: number;
  schemaVersion: number;
  engineVersion: string;
  sizeBytes: number;
}

export interface SaveRecord extends SaveMeta {
  /** Mundo serializado mais as decisões ainda não enviadas. */
  payload: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new ErroComChave("sessao.semArmazenamento"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("updatedAt", "updatedAt");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new ErroComChave("sessao.armazenamentoRecusou"));
  });
}

function run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = action(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new ErroComChave("sessao.armazenamentoRecusou"));
        tx.oncomplete = () => db.close();
      }),
  );
}

function stripPayload(record: SaveRecord): SaveMeta {
  const { payload: _payload, ...meta } = record;
  return meta;
}

export async function listSaves(): Promise<SaveMeta[]> {
  try {
    const records = await run<SaveRecord[]>("readonly", (store) => store.getAll());
    return records.map(stripPayload).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    // Aba anônima, cota cheia ou armazenamento bloqueado: a lista some, o jogo continua.
    return [];
  }
}

export async function readSave(id: string): Promise<SaveRecord | null> {
  const record = await run<SaveRecord | undefined>("readonly", (store) => store.get(id));
  return record ?? null;
}

export async function writeSave(record: SaveRecord): Promise<void> {
  await run("readwrite", (store) => store.put(record));
}

export async function deleteSave(id: string): Promise<void> {
  await run("readwrite", (store) => store.delete(id));
}

/** Mantém apenas os saves mais recentes, para o armazenamento não crescer sem fim. */
export async function pruneSaves(keep: number): Promise<void> {
  const metas = await listSaves();
  for (const meta of metas.slice(keep)) await deleteSave(meta.id);
}
